import { z } from "zod";

/**
 * Appointment draft validation (#2242).
 *
 * The Add/Edit Appointment modal used scattered inline regex checks and this
 * file was an empty Zod object, so every entry point could diverge and
 * malformed payloads reached the compatibility route. This is the single
 * validation authority: real calendar-date validation (not just shape),
 * time validity, and end-after-start ordering across midnight.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIME = /^\d{2}:\d{2}$/;

/** True only for a real calendar date (rejects 2026-02-30, month 13, …). */
export function isRealCalendarDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(Date.UTC(y!, m! - 1, d!));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === m! - 1 &&
    date.getUTCDate() === d
  );
}

export function isValidClockTime(value: string): boolean {
  if (!ISO_TIME.test(value)) return false;
  const [h, min] = value.split(":").map(Number);
  return h! <= 23 && min! <= 59;
}

export const addAppointmentSchema = z
  .object({
    title: z.string().trim().min(1, "title_required"),
    startDate: z.string().refine(isRealCalendarDate, "invalid_date"),
    startTime: z.string().refine(isValidClockTime, "invalid_time"),
    endDate: z.string().refine(isRealCalendarDate, "invalid_date"),
    endTime: z.string().refine(isValidClockTime, "invalid_time"),
  })
  .superRefine((draft, ctx) => {
    // Ordering: the end instant must be strictly after the start instant.
    // Cross-midnight appointments (endDate the next day) are valid.
    if (
      isRealCalendarDate(draft.startDate) &&
      isRealCalendarDate(draft.endDate) &&
      isValidClockTime(draft.startTime) &&
      isValidClockTime(draft.endTime)
    ) {
      const start = `${draft.startDate}T${draft.startTime}`;
      const end = `${draft.endDate}T${draft.endTime}`;
      if (end <= start) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["endTime"],
          message: "end_before_start",
        });
      }
    }
  });

export type AddAppointmentValues = z.infer<typeof addAppointmentSchema>;

export type AppointmentDraftField =
  | "title"
  | "startDate"
  | "startTime"
  | "endDate"
  | "endTime";

export type AppointmentDraftValidation =
  | { ok: true }
  | { ok: false; fieldErrors: Partial<Record<AppointmentDraftField, string>> };

/** Validate a draft before any mutation; first error per field wins. */
export function validateAppointmentDraft(draft: {
  title: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}): AppointmentDraftValidation {
  const result = addAppointmentSchema.safeParse(draft);
  if (result.success) return { ok: true };

  const fieldErrors: Partial<Record<AppointmentDraftField, string>> = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0];
    if (
      (field === "title" ||
        field === "startDate" ||
        field === "startTime" ||
        field === "endDate" ||
        field === "endTime") &&
      !fieldErrors[field]
    ) {
      fieldErrors[field] = issue.message;
    }
  }
  return { ok: false, fieldErrors };
}
